import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { PaymentService } from '@core/services/payment.service';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import { Payment, PaymentDetail, PaymentCreate, PaymentUpdate } from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';

@Injectable({ providedIn: 'root' })
export class PaymentQuery {
  private readonly paymentService = inject(PaymentService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  usePaymentsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const currentConfig = config();
      return {
        queryKey: [...QUERY_KEYS.payments.grid(currentConfig), condominiumId],
        queryFn: () => lastValueFrom(this.paymentService.getPayments(currentConfig)),
        select: (data: ApiResponse<PaginationResult<Payment>>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  usePaymentDetail(paymentId: () => string | null) {
    return injectQuery(() => {
      const id = paymentId();
      return {
        queryKey: QUERY_KEYS.payments.detail(id || ''),
        queryFn: () => lastValueFrom(this.paymentService.getPayment(id!)),
        select: (data: ApiResponse<PaymentDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  useRegisterPayment() {
    return injectMutation(() => ({
      mutationFn: (data: PaymentCreate) =>
        lastValueFrom(this.paymentService.registerPayment(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useUpdatePayment() {
    return injectMutation(() => ({
      mutationFn: (data: PaymentUpdate) =>
        lastValueFrom(this.paymentService.updatePayment(data)),
      onSuccess: (_result, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
      },
    }));
  }

  useDeletePayment() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.paymentService.deletePayment(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments.all });
        this.queryClient.removeQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }
}
